/**
 * Bond accrued coupon interest (ACI) for portfolio views — same logic as the
 * mobile app (`balances_calculator.dart` / `portfolio_holding.dart`).
 *
 * The client never computes ACI itself: the instrument entity carries
 * `bondAci`, the ACI of ONE bond in the instrument's `priceCurrencyId`
 * (`0` when nothing accrues). A position's ACI is that figure × quantity held.
 */

/** Minimal instrument fields needed to resolve ACI. */
export interface BondAciInstrument {
  instrumentInternalId?: string;
  priceCurrencyId?: string | null;
  bondAci?: number | string | null;
}

export interface BondAciInfo {
  bondAci: number;
  priceCurrency: string;
}

function toFiniteNumber(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * Builds `instrumentInternalId → { bondAci, priceCurrency }`, skipping
 * instruments without a `bondAci` (non-bonds).
 */
export function resolveBondAciById(
  instruments: BondAciInstrument[] | null | undefined
): Record<string, BondAciInfo> {
  const result: Record<string, BondAciInfo> = {};
  for (const inst of instruments ?? []) {
    const id = inst?.instrumentInternalId;
    const bondAci = toFiniteNumber(inst?.bondAci);
    if (!id || bondAci == null) continue;
    result[id] = {
      bondAci,
      priceCurrency: (inst.priceCurrencyId || '').toUpperCase(),
    };
  }
  return result;
}

/**
 * ACI of a whole position in the instrument's price currency, or `null` when
 * the instrument has no `bondAci` (so the UI can show "—").
 */
export function holdingAccruedInterest(
  quantity: number,
  bondAci: number | null | undefined
): number | null {
  if (bondAci == null) return null;
  const qty = Number.isFinite(quantity) ? quantity : 0;
  return qty * bondAci;
}

/**
 * Price-currency → USD rate, recovered from the backend's own valuation of the
 * position (`marketValueUsd = quantity × lastPrice × fx`), so ACI is converted
 * with exactly the rate the clean value used. Falls back to `1` when it cannot
 * be derived — the same silent fallback the mobile app applies.
 */
export function fxToUsdFromValuation(params: {
  currency: string | null | undefined;
  quantity: number | null | undefined;
  lastPrice: number | null | undefined;
  marketValueUsd: number | null | undefined;
}): number {
  const currency = (params.currency || '').toUpperCase();
  if (!currency || currency === 'USD') return 1;
  const qty = toFiniteNumber(params.quantity);
  const price = toFiniteNumber(params.lastPrice);
  const value = toFiniteNumber(params.marketValueUsd);
  if (qty == null || price == null || value == null) return 1;
  if (qty <= 0 || price <= 0 || value <= 0) return 1;
  return value / (qty * price);
}

/** Position ACI converted to USD: `quantity × bondAci × fx`. */
export function accruedInterestUsd(
  quantity: number,
  bondAci: number | null | undefined,
  fx: number
): number {
  const aci = holdingAccruedInterest(quantity, bondAci);
  return aci == null ? 0 : aci * fx;
}
